import { useCallback } from 'react';
import { useLoadingStore } from '@/stores/loadingStore';

interface UseLoadingStateOptions {
  key?: string;
  showLongLoadingMessage?: boolean;
  longLoadingThreshold?: number; // milliseconds
}

export const useLoadingState = (options: UseLoadingStateOptions = {}) => {
  const {
    key,
    showLongLoadingMessage = true,
    longLoadingThreshold = 5000
  } = options;

  const {
    globalLoading,
    globalMessage,
    globalProgress,
    setGlobalLoading,
    setLoading,
    updateProgress,
    clearLoading,
    clearAllLoading,
    isLoading,
    getLoadingState,
    getAllLoadingStates
  } = useLoadingStore();

  // Wrapper functions for easier usage
  const startLoading = useCallback((message?: string, progress?: number) => {
    if (key) {
      setLoading(key, true, message, progress);
    } else {
      setGlobalLoading(true, message, progress);
    }
  }, [key, setLoading, setGlobalLoading]);

  const stopLoading = useCallback(() => {
    if (key) {
      setLoading(key, false);
    } else {
      setGlobalLoading(false);
    }
  }, [key, setLoading, setGlobalLoading]);

  const setProgress = useCallback((progress: number) => {
    if (key) {
      updateProgress(key, progress);
    } else {
      setGlobalLoading(globalLoading, globalMessage, progress);
    }
  }, [key, updateProgress, globalLoading, globalMessage, setGlobalLoading]);

  // Get current loading state
  const currentState = key ? getLoadingState(key) : {
    loading: globalLoading,
    message: globalMessage,
    progress: globalProgress,
    duration: 0
  };

  const loading = key ? isLoading(key) : isLoading();

  // Check if loading has been going on for too long
  const isLongLoading = currentState && currentState.duration > longLoadingThreshold;
  const shouldShowLongLoadingMessage = showLongLoadingMessage && isLongLoading;

  // Enhanced message with long loading indication
  const enhancedMessage = shouldShowLongLoadingMessage && currentState?.message
    ? `${currentState.message} (This is taking longer than expected...)`
    : currentState?.message;

  return {
    // State
    loading,
    message: enhancedMessage,
    progress: currentState?.progress,
    duration: currentState?.duration || 0,
    isLongLoading: Boolean(isLongLoading),
    
    // Actions
    startLoading,
    stopLoading,
    setProgress,
    clearLoading: key ? () => clearLoading(key) : () => clearLoading(),
    clearAllLoading,
    
    // Utilities
    withLoading: <T extends any[], R>(
      fn: (...args: T) => Promise<R>,
      loadingMessage?: string
    ) => {
      return async (...args: T): Promise<R> => {
        try {
          startLoading(loadingMessage);
          const result = await fn(...args);
          return result;
        } finally {
          stopLoading();
        }
      };
    },
    
    // Debug info
    getAllStates: getAllLoadingStates
  };
};